import {
  createPlaceSearchHistory,
  deletePlaceSearchHistory,
  fetchLocationsAroundPosition,
  getPlaceSearchHistory,
} from "@/api/places";
import { fetchPlaceSuggestionResults } from "@/components/search-input/place-suggestion-service";
import { rememberLastMapCenter } from "@/utils/last-map-center";
import { isHighConfidencePlaceMatch } from "@/components/search-input/search-keyboard-navigation";
import PlaceRatingAndType from "@/components/place-mongo-search-dynamic-dropdown/place-rating-and-type";
import Iconify from "@/components/ui/Iconify";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { formatDistance } from "@/utils/helpers";
import { useTranslations } from "@/lib/static-intl";
import { buildPlaceDetailPath } from "@/constants/routes";
import { enqueueSnackbar } from "notistack";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

const hasTrailingHangulJamo = (value) =>
  /[\u3131-\u318e]$/.test(String(value || "").trim());

const NO_RESULT_SETTLE_DELAY_MS = 650;

const isTransientSuggestionError = (error) =>
  error?.code === "ECONNABORTED" ||
  error?.code === "ERR_CANCELED" ||
  /timeout|canceled|cancelled/i.test(String(error?.message || ""));

const PlacesListSearchBox = ({
  userLocation,
  mapCenter,
  searchQuery,
  setSearchQuery,
  addDistanceAttribute,
  completePlacesReset,
  addQueryParam,
  removeQueryParam,
}) => {
  const ref = useRef(null);
  const t = useTranslations("places");
  const apiT = useTranslations("api");
  const { user } = useAuthContext();
  const router = useRouter();

  const [searchInpVal, setSearchInpVal] = useState(searchQuery || "");
  const [debounceSearchInpVal, setDebounceSearchInpVal] = useState("");
  const [isSearchInpInFocus, setIsSearchInpInFocus] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchAutoCompletions, setSearchAutoCompletions] = useState([]);
  const [placesNearMe, setPlacesNearMe] = useState([]);
  const [searchLoaders, setSearchLoaders] = useState(false);
  const [isSearchComposing, setIsSearchComposing] = useState(false);
  const [settledSuggestionKey, setSettledSuggestionKey] = useState("");
  const [noResultReadyKey, setNoResultReadyKey] = useState("");
  const searchRequestIdRef = useRef(0);
  const lastNonEmptySearchRef = useRef({
    key: "",
    suggestions: [],
    completions: [],
  });

  const normalizeSuggestionKey = (value) =>
    String(value || "")
      .trim()
      .replace(/\s+/g, "")
      .toLowerCase();

  const placeSuggestionNameKeys = new Set(
    searchSuggestions.map((place) => normalizeSuggestionKey(place?.name))
  );
  const visibleSearchAutoCompletions = searchAutoCompletions.filter(
    (item) => !placeSuggestionNameKeys.has(normalizeSuggestionKey(item))
  );
  const currentSearchKey = normalizeSuggestionKey(searchInpVal);
  const isCurrentSearchInProgress =
    isSearchComposing || hasTrailingHangulJamo(searchInpVal);
  const shouldShowNoSearchResult =
    currentSearchKey.length > 0 &&
    settledSuggestionKey === currentSearchKey &&
    !searchLoaders &&
    noResultReadyKey === currentSearchKey &&
    !isCurrentSearchInProgress &&
    searchSuggestions.length === 0 &&
    visibleSearchAutoCompletions.length === 0;
  const isMatchingPlaceSuggestion = (query, place) =>
    Boolean(place?.exactPlaceMatch) || isHighConfidencePlaceMatch(query, place);

  const handleResetPlaces = () => {
    removeQueryParam("search-place");
    setSearchInpVal("");
    setSearchQuery("");
    setIsSearchInpInFocus(false);
    completePlacesReset();
  };

  const findMatchingPlaceForSubmit = async (query) => {
    const existingMatch = searchSuggestions.find((place) =>
      isMatchingPlaceSuggestion(query, place)
    );
    if (existingMatch) return existingMatch;

    try {
      const response = await fetchPlaceSuggestionResults({
        searchTerm: query,
        maxResults: 4,
        includeExecutionMeta: true,
      });
      const suggestions = response.placeSuggestions;

      return (
        suggestions.find((place) => isMatchingPlaceSuggestion(query, place)) ||
        null
      );
    } catch (_) {
      return null;
    }
  };

  const handlePlaceSearchSubmit = async (_query) => {
    const nextQuery = String(_query || "").trim();
    if (nextQuery?.length > 0) {
      if (nextQuery !== "My Location") handleCreateSearchHistory(nextQuery);

      const matchingPlace =
        nextQuery === "My Location"
          ? null
          : await findMatchingPlaceForSubmit(nextQuery);
      if (matchingPlace) {
        handlePlaceSearchTileClick(matchingPlace, nextQuery);
        return;
      }

      setSearchInpVal(nextQuery);
      removeQueryParam(["view-place", "view-place-in-list"]);
      setSearchQuery(nextQuery);
      setIsSearchInpInFocus(false);
    }
  };

  const handleDeletePlaceSearchHistory = async (placeIds) => {
    try {
      setSearchHistory((prev) =>
        prev?.filter((p) => !placeIds.includes(p?.id))
      );
      await deletePlaceSearchHistory([...placeIds]);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const handleCreateSearchHistory = async (searchQuery) => {
    try {
      if (!searchQuery || searchQuery?.trim()?.length === 0 || !user?.userId) {
        return;
      }
      const payload = {
        userId: user?.userId,
        search: searchQuery,
      };
      setSearchHistory((prev) => [payload, ...prev]);
      await createPlaceSearchHistory(payload);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const handlePlaceSearchTileClick = (place, submittedSearch) => {
    const placeId =
      place?._id || place?.id || place?.placeUniqueNumber || place?.publicId;
    const placePath = buildPlaceDetailPath(place);
    const fallbackPath = placeId ? `/place/${encodeURIComponent(placeId)}` : "";
    const targetPath =
      placePath && placePath !== "/places" ? placePath : fallbackPath;

    if (targetPath) {
      setSearchInpVal("");
      setIsSearchInpInFocus(false);
      router.push(targetPath);
      return;
    }

    if (placeId) {
      const nextSearch = submittedSearch || searchInpVal?.trim();
      addQueryParam({
        "search-place": nextSearch || null,
        "view-place-in-list": placeId,
      });
      setSearchInpVal("");
      setSearchQuery(nextSearch || "");
      setIsSearchInpInFocus(false);
    }
  };

  const handleSearchCompletionClick = async (completion) => {
    const normalizedCompletion = normalizeSuggestionKey(completion);
    const matchingPlace = searchSuggestions.find(
      (place) => normalizeSuggestionKey(place?.name) === normalizedCompletion
    );

    if (matchingPlace) {
      handlePlaceSearchTileClick(matchingPlace);
      return;
    }

    const fetchedMatch = await findMatchingPlaceForSubmit(completion);
    if (fetchedMatch) {
      handlePlaceSearchTileClick(fetchedMatch, completion);
      return;
    }

    handlePlaceSearchSubmit(completion);
  };

  const renderHighlightedText = (renderText, matchText, className) => {
    const matchChars = new Set(matchText.toLowerCase());

    const highlightedText = renderText.split("").map((char, index) => {
      const isMatch = matchChars.has(char.toLowerCase());

      return (
        <span
          key={index}
          style={{
            fontWeight: isMatch ? "bold" : "normal",
            color: isMatch ? "red" : "inherit",
          }}
        >
          {char}
        </span>
      );
    });

    return <span className={className}>{highlightedText}</span>;
  };

  const fetchUserSearchHistory = async () => {
    if (!user?.userId) return;
    try {
      const response = await getPlaceSearchHistory(user?.userId);
      if (Array.isArray(response)) setSearchHistory(response);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const fetchPlacesNearMe = async () => {
    if (!userLocation) return;
    try {
      const response = await fetchLocationsAroundPosition({
        position: {
          lat: userLocation?.latitude,
          lng: userLocation?.longitude,
        },
        maxResults: 10,
      });
      if (Array.isArray(response)) {
        setPlacesNearMe(addDistanceAttribute(response, userLocation));
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    }
  };

  const fetchPlacesSuggestions = async (searchTerm = debounceSearchInpVal) => {
    const nextSearch = String(searchTerm || "").trim();

    if (!nextSearch) {
      searchRequestIdRef.current += 1;
      setSearchSuggestions([]);
      setSearchAutoCompletions([]);
      setSettledSuggestionKey("");
      setNoResultReadyKey("");
      setSearchLoaders(false);
      return;
    }

    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    const requestKey = normalizeSuggestionKey(nextSearch);
    const getLastKnownResults = ({ allowPrevious = false } = {}) => {
      const cached = lastNonEmptySearchRef.current;
      if (cached.key === requestKey) return cached;
      if (allowPrevious && cached.key && requestKey.startsWith(cached.key)) {
        return cached;
      }
      return { suggestions: [], completions: [] };
    };

    try {
      setSearchLoaders(true);
      setNoResultReadyKey("");
      const searchOrigin = userLocation?.lat ? userLocation : mapCenter;
      // 검색 결과 화면(지도가 없는 곳)에서도 같은 기준을 쓰도록 남겨 둔다.
      rememberLastMapCenter(searchOrigin);
      const response = await fetchPlaceSuggestionResults({
        searchTerm: nextSearch,
        maxResults: 8,
        // 어디서 찾는지 서버에 알려준다 — 그래야 가까운 곳부터 고른다.
        // 위치 권한이 없으면 지금 보고 있는 지도 한가운데를 기준으로 삼는다.
        // 아무것도 안 보내면 서버는 전국에서 8곳을 골라, 서울에서 "갈비"를 쳐도
        // 238km 떨어진 대구 가게가 1등으로 올라온다.
        userLocation: searchOrigin,
      });
      const nextPlaceSuggestions = response.placeSuggestions;
      const nextPostSuggestions = response.postSearchSuggestions;

      if (requestId !== searchRequestIdRef.current) return;

      const distanceSuggestions = addDistanceAttribute(
        nextPlaceSuggestions,
        userLocation
      );

      if (distanceSuggestions.length > 0 || nextPostSuggestions.length > 0) {
        lastNonEmptySearchRef.current = {
          key: requestKey,
          suggestions: distanceSuggestions,
          completions: nextPostSuggestions,
        };
      }

      const lastKnownResults = getLastKnownResults({ allowPrevious: true });
      setSearchSuggestions(
        distanceSuggestions.length > 0
          ? distanceSuggestions
          : lastKnownResults.suggestions
      );
      setSearchAutoCompletions(
        nextPostSuggestions.length > 0
          ? nextPostSuggestions
          : lastKnownResults.completions
      );
      setSettledSuggestionKey(requestKey);
    } catch (error) {
      if (requestId === searchRequestIdRef.current) {
        const lastKnownResults = getLastKnownResults({ allowPrevious: true });
        setSearchSuggestions(lastKnownResults.suggestions);
        setSearchAutoCompletions(lastKnownResults.completions);
      }
      if (!isTransientSuggestionError(error)) {
        enqueueSnackbar(
          error?.response?.data?.message ||
            error?.message ||
            apiT("error message"),
          { variant: "error" }
        );
      }
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setSearchLoaders(false);
      }
    }
  };

  useEffect(() => {
    if (!String(searchInpVal || "").trim()) {
      searchRequestIdRef.current += 1;
      setDebounceSearchInpVal("");
      setSearchSuggestions([]);
      setSearchAutoCompletions([]);
      setSettledSuggestionKey("");
      setNoResultReadyKey("");
      setSearchLoaders(false);
      return undefined;
    }

    if (isCurrentSearchInProgress) {
      searchRequestIdRef.current += 1;
      setSearchLoaders(false);
      setNoResultReadyKey("");
      return undefined;
    }

    const handler = setTimeout(() => {
      setDebounceSearchInpVal(searchInpVal);
    }, 180);
    return () => clearTimeout(handler);
  }, [searchInpVal, isSearchComposing, isCurrentSearchInProgress]);

  useEffect(() => {
    if (debounceSearchInpVal && debounceSearchInpVal?.trim()?.length > 0) {
      fetchPlacesSuggestions(debounceSearchInpVal);
    }
  }, [debounceSearchInpVal]);

  useEffect(() => {
    if (
      !settledSuggestionKey ||
      settledSuggestionKey !== currentSearchKey ||
      searchLoaders ||
      isCurrentSearchInProgress ||
      searchSuggestions.length > 0 ||
      visibleSearchAutoCompletions.length > 0
    ) {
      setNoResultReadyKey("");
      return undefined;
    }

    const handler = setTimeout(() => {
      setNoResultReadyKey(settledSuggestionKey);
    }, NO_RESULT_SETTLE_DELAY_MS);

    return () => clearTimeout(handler);
  }, [
    settledSuggestionKey,
    currentSearchKey,
    searchLoaders,
    isCurrentSearchInProgress,
    searchSuggestions.length,
    visibleSearchAutoCompletions.length,
  ]);

  useEffect(() => {
    if (userLocation) {
      fetchPlacesNearMe();
    }
  }, [userLocation]);

  useEffect(() => {
    if (user?.userId) {
      fetchUserSearchHistory();
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsSearchInpInFocus(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      ref={ref}
      className="w-full bg-white dark:bg-custom_black px-2 py-2 pt-5 border-b dark:border-b-light_primary_label sticky -top-1 md:top-0 left-0 z-[35] flex items-center gap-2"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handlePlaceSearchSubmit(searchInpVal);
        }}
        className="w-full bg-[#f7f7f7] dark:bg-dark_bg_grey rounded-lg relative flex items-center pr-2"
      >
        {searchInpVal === "My Location" && (
          <Iconify
            icon={"tdesign:location-filled"}
            className={"!text-red ml-2 -mr-1"}
          />
        )}
        <input
          type="text"
          required
          name="place-search"
          placeholder={t("place search inp placeholder")}
          autoComplete="off"
          className="outline-none bg-transparent w-full py-2 px-3 text-sm caret-red placeholder:font-semibold placeholder:tracking-tight"
          value={searchInpVal}
          onChange={(e) => setSearchInpVal(e.target.value)}
          onCompositionStart={() => setIsSearchComposing(true)}
          onCompositionEnd={(e) => {
            setIsSearchComposing(false);
            setSearchInpVal(e.currentTarget.value);
          }}
          onFocus={() => {
            setIsSearchInpInFocus(true);
            if (
              searchInpVal?.trim()?.length > 0 &&
              searchSuggestions.length === 0 &&
              !searchLoaders &&
              !isCurrentSearchInProgress
            ) {
              fetchPlacesSuggestions(searchInpVal);
            }
          }}
        />
        {searchInpVal?.length > 0 && (
          <Iconify
            icon={"material-symbols-light:close"}
            className={""}
            onClick={handleResetPlaces}
          />
        )}
        {searchInpVal !== "My Location" && (
          <button
            type="submit"
            disabled={searchLoaders || searchInpVal?.length === 0}
          >
            <Iconify icon={"iconamoon:search-thin"} className={"ml-4 mr-2"} />
          </button>
        )}
        {isSearchInpInFocus && searchInpVal !== "My Location" && (
          <div className="absolute top-10 left-0 right-0 z-30 w-full border dark:border-brown rounded shadow shadow-dark_bg_grey/10 py-2 px-1 flex flex-col bg-white dark:bg-dark_bg_grey max-h-[300px] md:max-h-[500px] overflow-y-auto overflow-x-hidden thin-scrollbar">
            {searchInpVal?.length === 0 && !searchLoaders ? (
              <>
                <button
                  type="button"
                  className="w-full flex items-center gap-2 py-2 px-3 hover:bg-black/5 rounded text-sm transition ease-in-out duration-200 font-medium"
                  onClick={() => handlePlaceSearchSubmit("My Location")}
                >
                  <Iconify
                    icon={"material-symbols:my-location-outline"}
                    className={"!text-red"}
                  />
                  {t("search around me")}
                </button>
                <div className="w-full flex flex-col gap-1 my-2">
                  <div className="w-full flex items-center justify-between px-2">
                    <span className="text-black/40 dark:text-white/40 text-sm tracking-tight font-medium">
                      {t("recent search")}
                    </span>
                    <button
                      type="button"
                      className="text-black/40 dark:text-white/40 text-sm tracking-tight active:scale-95 transition"
                      onClick={() =>
                        handleDeletePlaceSearchHistory(
                          searchHistory?.map((_h) => _h?.id)
                        )
                      }
                    >
                      {t("clear search")}
                    </button>
                  </div>
                  {searchHistory?.slice(0, 5)?.map((history, index) => (
                    <div
                      key={index}
                      className="w-full px-2.5 py-2.5 flex items-center justify-between hover:bg-black/5 rounded transition ease-in-out duration-200 cursor-pointer"
                      onClick={() => handlePlaceSearchSubmit(history?.search)}
                    >
                      <div className="w-fit flex items-center gap-1.5 text-sm">
                        <Iconify
                          icon={"iconamoon:search-thin"}
                          className={"!w-4 !h-4"}
                        />
                        {history?.search}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (history?.id) {
                            handleDeletePlaceSearchHistory([history?.id]);
                          }
                        }}
                        className="hover:bg-black/10 rounded-full p-0.5 transition"
                      >
                        <Iconify
                          icon={"material-symbols-light:close"}
                          className={"!w-4 !h-4"}
                        />
                      </button>
                    </div>
                  ))}
                </div>
                {placesNearMe?.length > 0 && (
                  <span className="text-black/40 dark:text-white/40 text-sm tracking-tight font-medium px-2 mb-1">
                    {t("search suggestions")}
                  </span>
                )}
                {placesNearMe?.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    className="w-full flex flex-row gap-1 py-2 px-2 hover:bg-black/5 rounded transition ease-in-out duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlaceSearchTileClick(item);
                    }}
                  >
                    <span className="flex flex-col text-black/60 dark:text-white/60 items-center gap-1 md:gap-px">
                      <Iconify
                        icon={"mdi:location"}
                        className={"md:!w-8 md:!h-8 !text-inherit"}
                      />
                      {!isNaN(Number(item?.distance)) && (
                        <span className="text-[11px] text-black/90 dark:text-white/90 font-semibold tracking-tight leading-none">
                          {formatDistance(Number(item?.distance))}
                        </span>
                      )}
                    </span>
                    <div className="w-full flex flex-col gap-2 overflow-hidden">
                      <div className="w-full flex items-center justify-between gap-0">
                        {renderHighlightedText(
                          `${
                            item?.name ||
                            item?.city ||
                            item?.state ||
                            item?.country ||
                            ""
                          }`,
                          searchInpVal,
                          "text-sm max-w-[80%] truncate"
                        )}
                        <PlaceRatingAndType
                          key={item?._id || item?.id + index}
                          place={item}
                          placeType={item?.type}
                        />
                      </div>
                      {renderHighlightedText(
                        `${item?.address || item?.old_address || ""}`,
                        searchInpVal,
                        "text-xs max-w-[180px] sm:max-w-[200px] md:max-w-[90%] text-wrap break-words leading-tight text-start"
                      )}
                    </div>
                  </button>
                ))}
              </>
            ) : (
              <>
                {shouldShowNoSearchResult && (
                  <div className="w-full flex items-center justify-center gap-1 py-8 px-3 font-medium text-xs text-black/60 dark:text-white/60 tracking-tight text-nowrap">
                    {t("no search result")} <b>'{searchInpVal}'</b>
                  </div>
                )}
                {visibleSearchAutoCompletions?.length > 0 && (
                  <div className="w-full flex flex-col border-b dark:border-b-light_secondary_label pb-2">
                    {visibleSearchAutoCompletions?.slice(0, 10)?.map((item, index) => (
                      <div
                        key={index}
                        className="w-full px-2.5 py-2.5 flex items-center justify-start hover:bg-black/5 rounded transition ease-in-out duration-200 cursor-pointer"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSearchCompletionClick(item)}
                      >
                        <div className="w-fit flex items-center gap-1.5 text-sm">
                          <Iconify
                            icon={"iconamoon:search-thin"}
                            className={"!w-4 !h-4"}
                          />
                          <span>
                            {renderHighlightedText(
                              `${item}`,
                              searchInpVal,
                              "text-sm max-w-[180px] truncate"
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {searchSuggestions?.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    className="w-full flex flex-row gap-1 py-2 px-2 hover:bg-black/5 rounded transition ease-in-out duration-200"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlaceSearchTileClick(item);
                    }}
                  >
                    <span className="flex flex-col text-black/60 dark:text-white/60 items-center gap-1 md:gap-px">
                      <Iconify
                        icon={"mdi:location"}
                        className={"md:!w-8 md:!h-8 !text-inherit"}
                      />
                      {!isNaN(Number(item?.distance)) && (
                        <span className="text-[11px] text-black/90 dark:text-white/90 font-semibold tracking-tight leading-none">
                          {formatDistance(Number(item?.distance))}
                        </span>
                      )}
                    </span>
                    <div className="w-full flex flex-col gap-2 overflow-hidden">
                      <div className="w-full flex items-center justify-between gap-0">
                        {renderHighlightedText(
                          `${
                            item?.name ||
                            item?.city ||
                            item?.state ||
                            item?.country ||
                            ""
                          }`,
                          searchInpVal,
                          "text-sm max-w-[80%] truncate"
                        )}
                        <PlaceRatingAndType
                          key={item?._id || item?.id + index}
                          place={item}
                          placeType={item?.type}
                        />
                      </div>
                      {renderHighlightedText(
                        `${item?.address || item?.old_address || ""}`,
                        searchInpVal,
                        "text-xs max-w-[180px] sm:max-w-[200px] md:max-w-[90%] text-wrap break-words leading-tight text-start"
                      )}
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default PlacesListSearchBox;
